import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlanningContext } from '../state/PlanningContext';
import {
  countBucket,
  trackPlanningUpdatesEvent,
} from '../../../lib/planningUpdatesAnalytics';
import { 
  RefreshCw,
  Lightbulb,
  TrendingUp,
  Leaf,
  Store,
  Home,
  Building2,
  TreePine,
  Zap
} from 'lucide-react';

const PresetFilters = () => {
  const {
    setSelectedCategoryFilter,
    setSelectedScaleCategory,
    setSearchQuery,
    showToast,
    setMultipleCategoryFilters,
    multipleCategoryFilters = [],
    selectedCategoryFilter: _selectedCategoryFilter,
    selectedScaleCategory,
    setAdvancedScaleMode,
    setScaleRange,
    scaleRange,
    advancedScaleMode,
    categoryFilterMode,
    setCategoryFilterMode
  } = usePlanningContext();

  const [activePreset, setActivePreset] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [hoveredPreset, setHoveredPreset] = useState(null);

  const presets = useMemo(() => ([
    {
      id: 'investment',
      name: 'Investment Opportunities',
      icon: TrendingUp,
      accentColor: '#F59E0B',
      glowColor: 'rgba(255, 215, 0, 0.35)',
      description: 'Major developments & high-impact projects',
      categories: ['new_build_major', 'demolition', 'legal_admin'],
      scaleRating: { min: 5, max: 10, label: '5-10' },
      searchTerms: ['development', 'construction', 'site'],
      useAdvancedScale: true
    },
    {
      id: 'renewables',
      name: 'Green & Renewable',
      icon: Leaf,
      accentColor: '#10B981',
      glowColor: 'rgba(255, 191, 0, 0.35)',
      description: 'Sustainable energy & eco-friendly projects',
      categories: ['renewables_energy', 'mechanical_services'],
      searchTerms: ['solar', 'renewable', 'energy', 'sustainable']
    },
    {
      id: 'retail',
      name: 'Retail & Commerce',
      icon: Store,
      accentColor: '#8B5CF6',
      glowColor: 'rgba(218, 165, 32, 0.35)',
      description: 'Shop fronts & commercial transformations',
      categories: ['shopfront_signage', 'external_lighting'],
      scaleRating: { min: 2, max: 10, label: '2-10' },
      searchTerms: ['shop', 'retail', 'commercial', 'business'],
      useAdvancedScale: true
    },
    {
      id: 'residential',
      name: 'Residential Upgrades',
      icon: Home,
      accentColor: '#3B82F6',
      glowColor: 'rgba(255, 223, 96, 0.35)',
      description: 'Home improvements & extensions',
      categories: ['extensions_enlargements', 'roof_works_dormers', 'windows_doors_glazing'],
      scaleRating: { min: 1, max: 10, label: '1-10' },
      searchTerms: ['residential', 'home', 'house', 'dwelling'],
      useAdvancedScale: true
    },
    {
      id: 'infrastructure',
      name: 'Infrastructure & Access',
      icon: Building2,
      accentColor: '#64748B',
      glowColor: 'rgba(184, 134, 11, 0.35)',
      description: 'Transport, parking & utility projects',
      categories: ['parking_access', 'mechanical_services', 'external_lighting'],
      scaleRating: { min: 4, max: 10, label: '4-10' },
      searchTerms: ['infrastructure', 'parking', 'access', 'highway'],
      useAdvancedScale: true
    },
    {
      id: 'nature',
      name: 'Nature & Landscaping',
      icon: TreePine,
      accentColor: '#14B8A6',
      glowColor: 'rgba(251, 191, 36, 0.35)',
      description: 'Trees, ecology & green spaces',
      categories: ['trees_ecology', 'landscaping_boundaries'],
      scaleRating: { min: 2, max: 10, label: '2-10' },
      searchTerms: ['trees', 'garden', 'landscaping', 'ecology'],
      useAdvancedScale: true
    }
  ]), []);

  // Check if any preset should be considered active based on current filters
  useEffect(() => {
    if (isApplying) return; // Don't change active preset while applying
    
    const currentPreset = presets.find(preset => {
      // Check if categories match (now only using multipleCategoryFilters)
      const categoriesMatch = 
        multipleCategoryFilters.length === preset.categories.length &&
        preset.categories.every(cat => multipleCategoryFilters.includes(cat)) &&
        categoryFilterMode === 'include'; // Presets only work in include mode
      
      // Check if scale matches (for presets with scale)
      let scaleMatches = true;
      if (preset.useAdvancedScale && preset.scaleRating) {
        scaleMatches = advancedScaleMode && 
                      scaleRange.min === preset.scaleRating.min && 
                      scaleRange.max === preset.scaleRating.max;
      } else if (!preset.scaleRating) {
        // For presets without scale (like green), just check categories
        scaleMatches = true;
      }
      
      return categoriesMatch && scaleMatches;
    });
    
    setActivePreset(currentPreset ? currentPreset.id : null);
  }, [multipleCategoryFilters, categoryFilterMode, selectedScaleCategory, advancedScaleMode, scaleRange, isApplying, presets]);

  const applyPreset = async (preset) => {
    if (isApplying) return;
    
    trackPlanningUpdatesEvent('planning_updates_quick_preset_selected', {
      placement: 'quick_presets',
      status: preset.id,
      value: preset.categories.length,
      metadata: {
        preset_id: preset.id,
        category_count: preset.categories.length,
        category_bucket: countBucket(preset.categories.length),
        has_scale_rating: Boolean(preset.scaleRating),
        scale_min: preset.scaleRating?.min ?? 1,
        scale_max: preset.scaleRating?.max ?? 10
      }
    });

    setIsApplying(true);
    setActivePreset(preset.id);

    // Clear search query first
    setSearchQuery('');

    // Animate the application
    await new Promise(resolve => setTimeout(resolve, 300));

    // Apply the preset filters
    // Always use include mode for presets and use multipleCategoryFilters
    setCategoryFilterMode('include');
    setMultipleCategoryFilters(preset.categories);
    setSelectedCategoryFilter(null);

    // Set scale rating using advanced mode if preset has scale
    if (preset.useAdvancedScale && preset.scaleRating) {
      setAdvancedScaleMode(true);
      setScaleRange({
        min: preset.scaleRating.min,
        max: preset.scaleRating.max
      });
      setSelectedScaleCategory('all'); // Reset basic scale category
    } else if (!preset.scaleRating) {
      // For presets without scale (like green), just check categories
      setAdvancedScaleMode(false);
      setSelectedScaleCategory('all');
    }

    showToast(`Applied "${preset.name}" preset`, 'success');
    
    setTimeout(() => {
      setIsApplying(false);
    }, 500);
  };

  const clearPreset = () => {
    trackPlanningUpdatesEvent('planning_updates_quick_preset_cleared', {
      placement: 'quick_presets',
      status: activePreset || 'unknown',
      metadata: {
        preset_id: activePreset || 'unknown',
        category_count: multipleCategoryFilters.length,
        category_bucket: countBucket(multipleCategoryFilters.length)
      }
    });
    setActivePreset(null);
    setSelectedCategoryFilter(null);
    if (setMultipleCategoryFilters) {
      setMultipleCategoryFilters([]);
    }
    setCategoryFilterMode('include');
    setSelectedScaleCategory('all');
    setAdvancedScaleMode(false);
    setScaleRange({ min: 1, max: 10 });
    setSearchQuery('');
    showToast('Preset cleared', 'info');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-8"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 relative flex items-center justify-center">
            <Zap className="w-6 h-6 text-[var(--c-accent)]" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-[var(--c-text-primary)] mb-1">Quick Presets</h3>
            <p className="text-sm text-[var(--c-text-secondary)]">
              Instantly filter to specific project types
            </p>
          </div>
        </div>
        
        {activePreset && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={clearPreset}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-[var(--c-surface)] border border-[var(--c-border)] text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] hover:bg-[var(--c-hover)] transition-all duration-200 shadow-sm"
          >
            <RefreshCw size={16} />
            Clear Preset
          </motion.button>
        )}
      </div>

            {/* Enhanced Technical Icon Grid */}
      <div className="relative">
        {/* Subtle grid background */}
        <div 
          className="absolute inset-0 -m-4 opacity-[0.02] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, var(--c-text-primary) 1px, transparent 1px)`,
            backgroundSize: '24px 24px'
          }}
        />
        
        <div className="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-10 gap-y-16 mb-8 px-4 py-6">
        <AnimatePresence>
          {presets.map((preset, index) => {
            const isActive = activePreset === preset.id;
            const isHovered = hoveredPreset === preset.id;
            const IconComponent = preset.icon;

            return (
              <motion.div
                key={preset.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 30 }}
                transition={{ 
                  delay: index * 0.08,
                  duration: 0.5,
                  ease: 'easeOut'
                }}
                onMouseEnter={() => setHoveredPreset(preset.id)}
                onMouseLeave={() => setHoveredPreset(null)}
                onClick={() => applyPreset(preset)}
                className="relative flex flex-col items-center cursor-pointer group"
                style={isHovered ? { zIndex: 10 } : undefined}
              >
                {/* Technical Icon Container */}
                <motion.div 
                  className="relative mb-4 w-24 h-24 flex items-center justify-center"
                  animate={isActive ? {
                    scale: [1, 1.08, 1],
                    y: [0, -4, 0]
                  } : isHovered ? {
                    scale: 1.12,
                    y: -6
                  } : {
                    scale: 1,
                    y: 0
                  }}
                  transition={{
                    duration: isActive ? 2.5 : 0.4,
                    repeat: isActive ? Infinity : 0,
                    ease: 'easeInOut'
                  }}
                >
                  {/* Floating Particles for Active State */}
                  {isActive && (
                    <>
                      {[...Array(6)].map((_, i) => (
                        <motion.div
                          key={`particle-${i}`}
                          className="absolute w-1 h-1 rounded-full"
                          style={{
                            backgroundColor: preset.accentColor,
                            top: '50%',
                            left: '50%'
                          }}
                          animate={{
                            x: [0, Math.cos((i * 60 * Math.PI) / 180) * 40],
                            y: [0, Math.sin((i * 60 * Math.PI) / 180) * 40],
                            opacity: [0, 0.8, 0],
                            scale: [0, 1, 0]
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            delay: i * 0.5,
                            ease: 'easeInOut'
                          }}
                        />
                      ))}
                    </>
                  )}

                  {/* Enhanced Active Glow Ring */}
                  {isActive && (
                    <>
                      {/* Primary glow with breathing effect */}
                    <motion.div
                      className="absolute inset-0 -m-6 rounded-full"
                      style={{
                          background: `radial-gradient(circle, ${preset.glowColor} 0%, ${preset.accentColor}15 40%, transparent 70%)`
                        }}
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.6, 0.9, 0.6]
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: 'easeInOut'
                        }}
                      />
                      
                      {/* Secondary glow with offset timing */}
                      <motion.div
                        className="absolute inset-0 -m-4 rounded-full"
                        style={{
                          background: `radial-gradient(circle, ${preset.accentColor}20 0%, transparent 60%)`
                      }}
                      animate={{
                          scale: [1.1, 1.4, 1.1],
                          opacity: [0.4, 0.7, 0.4]
                      }}
                      transition={{
                        duration: 3,
                          repeat: Infinity,
                          ease: 'easeInOut',
                          delay: 1
                        }}
                      />

                      {/* Orbital ring effect */}
                      <motion.div
                        className="absolute inset-0 -m-8 border rounded-full"
                        style={{ 
                          borderColor: `${preset.accentColor}20`,
                          borderWidth: '1px'
                        }}
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          ease: 'linear'
                        }}
                      >
                        <motion.div
                          className="absolute w-2 h-2 rounded-full -top-1 left-1/2 transform -translate-x-1/2"
                          style={{ backgroundColor: preset.accentColor }}
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.6, 1, 0.6]
                          }}
                          transition={{
                            duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }}
                    />
                      </motion.div>
                    </>
                  )}

                  {/* Enhanced Hover Glow */}
                  {isHovered && !isActive && (
                    <>
                      {/* Primary hover glow */}
                    <motion.div
                      className="absolute inset-0 -m-4 rounded-full"
                      style={{
                          background: `radial-gradient(circle, ${preset.glowColor} 0%, ${preset.accentColor}10 50%, transparent 70%)`
                      }}
                      initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 0.7, scale: 1.1 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                      />
                      
                      {/* Secondary hover glow */}
                      <motion.div
                        className="absolute inset-0 -m-2 rounded-full"
                        style={{
                          background: `radial-gradient(circle, ${preset.accentColor}15 0%, transparent 50%)`
                        }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 0.5, scale: 1.05 }}
                        transition={{ duration: 0.3, ease: 'easeOut', delay: 0.1 }}
                      />

                      {/* Hover scan line effect */}
                      <motion.div
                        className="absolute inset-0 rounded-xl overflow-hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <motion.div
                          className="absolute w-full h-0.5 rounded-full"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${preset.accentColor}, transparent)`,
                            filter: `drop-shadow(0 0 4px ${preset.accentColor})`
                          }}
                          animate={{
                            top: ['-10%', '110%']
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut'
                          }}
                        />
                      </motion.div>
                    </>
                  )}

                  {/* Ultra-Enhanced Technical Icon Background */}
                  <motion.div
                    className="relative w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden group"
                    style={{
                      background: isActive 
                        ? `linear-gradient(135deg, ${preset.accentColor}25, ${preset.accentColor}15, ${preset.accentColor}05)`
                        : `linear-gradient(135deg, var(--c-surface), rgba(255,255,255,0.02), var(--c-surface))`,
                      border: isActive 
                        ? `1px solid ${preset.accentColor}80`
                        : '1px solid var(--c-border)',
                      boxShadow: isActive 
                        ? `0 12px 40px -12px ${preset.glowColor}, 0 0 0 1px ${preset.accentColor}60, inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.1)`
                        : isHovered 
                          ? `0 8px 24px -8px ${preset.glowColor}, 0 0 0 1px ${preset.accentColor}30, inset 0 1px 0 rgba(255,255,255,0.08)`
                          : '0 4px 12px -4px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(0,0,0,0.05)',
                      backdropFilter: 'blur(12px) saturate(130%)'
                    }}
                    whileHover={{
                      boxShadow: `0 16px 48px -16px ${preset.glowColor}, 0 0 0 1px ${preset.accentColor}50, inset 0 1px 0 rgba(255,255,255,0.2)`
                    }}
                    whileTap={{
                      scale: 0.95,
                      transition: { duration: 0.1 }
                    }}
                  >
                    {/* Enhanced Background Pattern */}
                    <div 
                      className="absolute inset-0 opacity-[0.04]"
                      style={{
                        backgroundImage: `
                          radial-gradient(circle at 25% 25%, ${preset.accentColor} 1px, transparent 1px),
                          linear-gradient(90deg, ${preset.accentColor}10 1px, transparent 1px),
                          linear-gradient(${preset.accentColor}10 1px, transparent 1px)
                        `,
                        backgroundSize: '8px 8px, 16px 16px, 16px 16px'
                      }}
                    />
                    
                    {/* Circuit-like corner accents */}
                    <div className="absolute top-1 left-1 w-2 h-2 border-l border-t opacity-20" style={{ borderColor: preset.accentColor }} />
                    <div className="absolute top-1 right-1 w-2 h-2 border-r border-t opacity-20" style={{ borderColor: preset.accentColor }} />
                    <div className="absolute bottom-1 left-1 w-2 h-2 border-l border-b opacity-20" style={{ borderColor: preset.accentColor }} />
                    <div className="absolute bottom-1 right-1 w-2 h-2 border-r border-b opacity-20" style={{ borderColor: preset.accentColor }} />
                    
                    {/* Inner Glow Layer with enhanced depth */}
                    {isActive && (
                      <div 
                        className="absolute inset-0 rounded-xl"
                        style={{
                          background: `radial-gradient(circle at 30% 30%, ${preset.accentColor}20 0%, ${preset.accentColor}10 40%, transparent 70%)`,
                        }}
                      />
                    )}
                    
                    {/* Enhanced Icon with better positioning */}
                    <IconComponent 
                      className="relative z-10 w-9 h-9 transition-all duration-500"
                      style={{
                        color: isActive ? preset.accentColor : 'var(--c-text-primary)',
                        filter: isActive 
                          ? `drop-shadow(0 0 16px ${preset.glowColor}) drop-shadow(0 2px 4px rgba(0,0,0,0.3)) brightness(1.15) contrast(1.1)` 
                          : isHovered
                            ? `drop-shadow(0 0 8px ${preset.glowColor}) drop-shadow(0 1px 2px rgba(0,0,0,0.2)) brightness(1.05)`
                            : 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                        strokeWidth: isActive ? '1.75' : '1.5',
                        transform: isActive ? 'translateY(-1px)' : 'none'
                      }}
                    />
                    
                    {/* Premium Reflection Effect */}
                    <div 
                      className="absolute top-0 left-0 right-0 h-1/2 rounded-t-xl pointer-events-none"
                      style={{
                        background: isActive 
                          ? `linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, transparent 100%)`
                          : `linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 50%, transparent 100%)`
                      }}
                    />

                    {/* Subtle edge lighting */}
                    {isActive && (
                      <div 
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{
                          boxShadow: `inset 0 0 0 1px ${preset.accentColor}30`
                        }}
                      />
                    )}
                  </motion.div>

                  {/* Enhanced Active Pulse Ring */}
                  {isActive && (
                    <>
                      {/* Outer pulse ring */}
                      <motion.div
                        className="absolute inset-0 -m-3 rounded-xl border"
                        style={{ 
                          borderColor: `${preset.accentColor}60`,
                          background: `radial-gradient(circle, ${preset.accentColor}08 0%, transparent 70%)`
                        }}
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.8, 0.3, 0.8]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: 'easeInOut'
                        }}
                      />
                      
                      {/* Inner pulse ring */}
                    <motion.div
                        className="absolute inset-0 -m-1 rounded-xl border"
                        style={{ borderColor: preset.accentColor }}
                      animate={{
                          scale: [1, 1.05, 1],
                          opacity: [0.6, 0, 0.6]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                          ease: 'easeInOut',
                          delay: 0.5
                      }}
                    />
                    </>
                  )}

                  {/* Enhanced Loading State */}
                  {isApplying && activePreset === preset.id && (
                    <motion.div
                      className="absolute inset-0 -m-2 flex items-center justify-center rounded-xl border"
                      style={{
                        background: `linear-gradient(135deg, var(--c-surface)95, ${preset.accentColor}10)`,
                        borderColor: `${preset.accentColor}40`,
                        backdropFilter: 'blur(12px) saturate(120%)',
                        boxShadow: `0 4px 20px -4px ${preset.glowColor}, inset 0 1px 0 rgba(255,255,255,0.1)`
                      }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <div className="flex flex-col items-center gap-2">
                        {/* Enhanced loading spinner */}
                        <motion.div
                          className="relative w-8 h-8"
                        >
                          {/* Outer ring */}
                          <motion.div
                            className="absolute inset-0 border-2 border-t-transparent rounded-full"
                            style={{ borderColor: `${preset.accentColor}40` }}
                          animate={{ rotate: 360 }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              ease: 'linear'
                            }}
                          />
                          {/* Inner ring */}
                          <motion.div
                            className="absolute inset-1 border-2 border-b-transparent rounded-full"
                            style={{ borderColor: preset.accentColor }}
                            animate={{ rotate: -360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: 'linear'
                          }}
                        />
                          {/* Center dot */}
                          <motion.div
                            className="absolute top-1/2 left-1/2 w-1 h-1 rounded-full transform -translate-x-1/2 -translate-y-1/2"
                            style={{ backgroundColor: preset.accentColor }}
                            animate={{
                              scale: [1, 1.5, 1],
                              opacity: [0.5, 1, 0.5]
                            }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: 'easeInOut'
                            }}
                          />
                        </motion.div>
                        
                        <motion.span 
                          className="text-xs font-medium tracking-wide"
                          style={{ color: preset.accentColor }}
                          animate={{
                            opacity: [0.7, 1, 0.7]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut'
                          }}
                        >
                          Loading...
                        </motion.span>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
                
                {/* Enhanced Typography with Status Indicator */}
                <motion.div
                  className="text-center max-w-[140px] space-y-2"
                  animate={isActive ? {
                    color: preset.accentColor
                  } : {}}
                  transition={{ duration: 0.3 }}
                >
                  <motion.h4 
                    className="font-bold text-sm leading-tight text-[var(--c-text-primary)] tracking-wide"
                    style={{
                      fontFamily: "'Space Grotesk', system-ui, sans-serif",
                      fontWeight: isActive ? '700' : '600',
                      color: isActive ? preset.accentColor : 'var(--c-text-primary)',
                      textShadow: isActive ? `0 0 8px ${preset.glowColor}` : 'none'
                    }}
                    animate={isActive ? {
                      scale: [1, 1.02, 1],
                      y: [0, -1, 0]
                    } : {}}
                    transition={{
                      duration: 2,
                      repeat: isActive ? Infinity : 0
                    }}
                  >
                    {preset.name}
                  </motion.h4>
                  
                  {/* Status Line */}
                  <motion.div
                    className="flex items-center justify-center gap-1.5"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <motion.div
                      className="w-1 h-1 rounded-full"
                      style={{
                        backgroundColor: isActive 
                          ? preset.accentColor 
                          : isHovered 
                            ? preset.accentColor + '80'
                            : 'var(--c-text-secondary)'
                      }}
                      animate={isActive ? {
                        scale: [1, 1.5, 1],
                        opacity: [0.6, 1, 0.6]
                      } : {}}
                      transition={{
                        duration: 2,
                        repeat: isActive ? Infinity : 0,
                        ease: 'easeInOut'
                      }}
                    />
                    <span 
                      className="text-xs font-medium tracking-wider uppercase"
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        color: isActive 
                          ? preset.accentColor 
                          : isHovered 
                            ? preset.accentColor + '80'
                            : 'var(--c-text-secondary)',
                        fontSize: '10px',
                        letterSpacing: '0.8px'
                      }}
                    >
                      {isActive ? 'ACTIVE' : preset.categories.length + ' CATS'}
                    </span>
                    <motion.div
                      className="w-1 h-1 rounded-full"
                      style={{
                        backgroundColor: isActive 
                          ? preset.accentColor 
                          : isHovered 
                            ? preset.accentColor + '80'
                            : 'var(--c-text-secondary)'
                      }}
                      animate={isActive ? {
                        scale: [1, 1.5, 1],
                        opacity: [0.6, 1, 0.6]
                      } : {}}
                      transition={{
                        duration: 2,
                        repeat: isActive ? Infinity : 0,
                        ease: 'easeInOut',
                        delay: 0.5
                      }}
                    />
                  </motion.div>
                </motion.div>

                {/* Premium Active Indicator Badge */}
                {isActive && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    className="absolute -top-2 -right-2 z-20"
                  >
                    {/* Badge background with glow */}
                    <motion.div
                      className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold shadow-lg border backdrop-blur-sm"
                      style={{
                        backgroundColor: preset.accentColor + 'E6',
                        borderColor: preset.accentColor,
                        color: '#000',
                        boxShadow: `0 4px 16px -4px ${preset.glowColor}, 0 0 0 1px ${preset.accentColor}60`,
                        fontFamily: "'DM Mono', monospace",
                        letterSpacing: '0.5px'
                      }}
                      animate={{ 
                        boxShadow: [
                          `0 4px 16px -4px ${preset.glowColor}, 0 0 0 1px ${preset.accentColor}60`,
                          `0 6px 20px -4px ${preset.glowColor}, 0 0 0 1px ${preset.accentColor}80`,
                          `0 4px 16px -4px ${preset.glowColor}, 0 0 0 1px ${preset.accentColor}60`
                        ]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }}
                    >
                      {/* Animated pulse dot */}
                      <motion.div
                        className="w-1.5 h-1.5 bg-black rounded-full"
                        animate={{ 
                          scale: [1, 1.3, 1],
                          opacity: [1, 0.7, 1]
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: 'easeInOut'
                        }}
                      />
                      <span>ACTIVE</span>
                      
                      {/* Corner accent */}
                      <div 
                        className="absolute -top-0.5 -right-0.5 w-2 h-2 border-r border-t rounded-tr"
                        style={{ borderColor: preset.accentColor }}
                      />
                    </motion.div>
                  </motion.div>
                )}

                {/* Premium Technical Tooltip */}
                <AnimatePresence>
                  {isHovered && !isApplying && (
                    <motion.div
                      initial={{ opacity: 0, y: 15, scale: 0.9, x: '-50%' }}
                      animate={{ opacity: 1, y: 0, scale: 1, x: '-50%' }}
                      exit={{ opacity: 0, y: 15, scale: 0.9, x: '-50%' }}
                      className="absolute top-full left-1/2 min-w-[320px] max-w-[360px] z-[1000] mt-4"
                      style={{ pointerEvents: 'none' }}
                    >
                      {/* Tooltip container with premium styling */}
                      <motion.div
                        className="relative rounded-xl border overflow-hidden"
                        style={{
                          background: `linear-gradient(135deg, var(--c-surface)95, ${preset.accentColor}08)`,
                          borderColor: `${preset.accentColor}40`,
                          boxShadow: `
                            0 12px 32px -8px ${preset.glowColor}, 
                            0 0 0 1px ${preset.accentColor}30,
                            inset 0 1px 0 rgba(255,255,255,0.1),
                            inset 0 -1px 0 rgba(0,0,0,0.1)
                          `
                        }}
                        initial={{ 
                          rotateX: -10,
                          backdropFilter: 'blur(16px) saturate(140%)'
                        }}
                        animate={{ 
                          rotateX: 0,
                          backdropFilter: 'blur(16px) saturate(140%)'
                        }}
                        exit={{
                          backdropFilter: 'blur(16px) saturate(140%)'
                        }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      >
                        {/* Animated scan line */}
                        <motion.div
                          className="absolute top-0 left-0 right-0 h-0.5"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${preset.accentColor}, transparent)`,
                            filter: `drop-shadow(0 0 4px ${preset.accentColor})`
                          }}
                          animate={{
                            left: ['-100%', '100%']
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                            delay: 1
                          }}
                        />

                        {/* Header section */}
                        <div className="p-4 pb-3 border-b" style={{ borderColor: `${preset.accentColor}20` }}>
                          <div className="flex items-center gap-3 mb-2">
                            <motion.div
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{
                                background: `linear-gradient(135deg, ${preset.accentColor}20, ${preset.accentColor}10)`,
                                border: `1px solid ${preset.accentColor}40`
                              }}
                              animate={{
                                boxShadow: [
                                  `0 0 0 0px ${preset.accentColor}20`,
                                  `0 0 0 4px ${preset.accentColor}10`,
                                  `0 0 0 0px ${preset.accentColor}20`
                                ]
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut'
                              }}
                            >
                              <IconComponent 
                                className="w-4 h-4" 
                                style={{ 
                                  color: preset.accentColor,
                                  filter: `drop-shadow(0 0 4px ${preset.glowColor})`
                                }} 
                              />
                            </motion.div>
                            <div>
                              <h4 
                                className="font-bold text-base leading-tight"
                                style={{
                                  color: preset.accentColor,
                                  fontFamily: "'Space Grotesk', system-ui, sans-serif",
                                  textShadow: `0 0 8px ${preset.glowColor}`
                                }}
                              >
                                {preset.name}
                              </h4>
                              <div className="flex items-center gap-2 mt-1">
                                <motion.div
                                  className="w-1 h-1 rounded-full"
                                  style={{ backgroundColor: preset.accentColor }}
                                  animate={{
                                    scale: [1, 1.5, 1],
                                    opacity: [0.6, 1, 0.6]
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: 'easeInOut'
                                  }}
                                />
                                <span 
                                  className="text-xs font-medium tracking-wider uppercase"
                                  style={{
                                    fontFamily: "'DM Mono', monospace",
                                    color: `${preset.accentColor}CC`,
                                    fontSize: '10px'
                                  }}
                                >
                                  PRESET FILTER
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <p 
                            className="text-sm leading-relaxed"
                            style={{ color: 'var(--c-text-primary)' }}
                          >
                          {preset.description}
                        </p>
                        </div>

                        {/* Stats section */}
                        <div className="p-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            {/* Categories stat */}
                            <motion.div
                              className="relative p-3 rounded-lg border"
                              style={{
                                background: `linear-gradient(135deg, ${preset.accentColor}10, transparent)`,
                                borderColor: `${preset.accentColor}20`
                              }}
                              whileHover={{ scale: 1.02 }}
                            >
                          <div className="flex items-center justify-between">
                                <span 
                                  className="text-xs font-medium tracking-wide uppercase"
                                  style={{
                                    fontFamily: "'DM Mono', monospace",
                                    color: 'var(--c-text-secondary)'
                                  }}
                                >
                                  Categories
                            </span>
                                <motion.div
                                  className="px-2 py-1 rounded-md text-xs font-bold"
                                  style={{
                                    backgroundColor: `${preset.accentColor}20`,
                                    color: preset.accentColor,
                                    fontFamily: "'DM Mono', monospace"
                                  }}
                                  animate={{
                                    backgroundColor: [
                                      `${preset.accentColor}20`,
                                      `${preset.accentColor}30`,
                                      `${preset.accentColor}20`
                                    ]
                                  }}
                                  transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: 'easeInOut'
                                  }}
                                >
                                  {preset.categories.length}
                                </motion.div>
                          </div>
                            </motion.div>

                            {/* Scale range stat */}
                          {preset.scaleRating && (
                              <motion.div
                                className="relative p-3 rounded-lg border"
                                style={{
                                  background: `linear-gradient(135deg, ${preset.accentColor}10, transparent)`,
                                  borderColor: `${preset.accentColor}20`
                                }}
                                whileHover={{ scale: 1.02 }}
                              >
                            <div className="flex items-center justify-between">
                                  <span 
                                    className="text-xs font-medium tracking-wide uppercase"
                                    style={{
                                      fontFamily: "'DM Mono', monospace",
                                      color: 'var(--c-text-secondary)'
                                    }}
                                  >
                                    Scale
                                  </span>
                                  <motion.div
                                    className="px-2 py-1 rounded-md text-xs font-bold"
                                    style={{
                                      backgroundColor: `${preset.accentColor}20`,
                                      color: preset.accentColor,
                                      fontFamily: "'DM Mono', monospace"
                                    }}
                                  >
                                {preset.scaleRating.label}
                                  </motion.div>
                            </div>
                              </motion.div>
                            )}
                          </div>

                          {/* Keywords section */}
                          <div className="space-y-2">
                            <span 
                              className="text-xs font-medium tracking-wide uppercase block"
                              style={{
                                fontFamily: "'DM Mono', monospace",
                                color: 'var(--c-text-secondary)'
                              }}
                            >
                              Keywords
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {preset.searchTerms.slice(0, 4).map((term, i) => (
                                <motion.span
                                  key={term}
                                  className="px-2 py-1 rounded-md text-xs font-medium"
                                  style={{
                                    backgroundColor: `${preset.accentColor}15`,
                                    color: preset.accentColor,
                                    border: `1px solid ${preset.accentColor}30`,
                                    fontFamily: "'Inter', system-ui, sans-serif"
                                  }}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: i * 0.1, duration: 0.2 }}
                                >
                                  {term}
                                </motion.span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Bottom accent line */}
                        <div 
                          className="h-0.5"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${preset.accentColor}, transparent)`
                          }}
                        />
                      </motion.div>

                      {/* Tooltip arrow */}
                      <div 
                        className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 rotate-45 border-l border-t"
                        style={{
                          background: `linear-gradient(135deg, var(--c-surface), ${preset.accentColor}08)`,
                          borderColor: `${preset.accentColor}40`
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
        </div>
      </div>

      {/* Clean Info Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="p-4 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-xl"
      >
        <div className="flex items-center gap-3">
          <Lightbulb size={16} className="text-[var(--c-accent)]" />
          <p className="text-sm text-[var(--c-text-secondary)]">
            Hover over presets to see details. Click to apply filters instantly.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PresetFilters;
